import React, { useContext } from 'react';
import useForm from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import { ExternalOrdersApiTypeEnum, SnPartyConfigDTO } from '@api/ringil-api.v2';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import useSidebarControl, { SidebarControlProps } from '@hooks/useSidebarControl/useSidebarControl.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { createOrdersComponentValidationSchema } from '@components/framework/orders/validation.ts';
import { buildSnManUnitTestId, buildSnOrderTestId, generateSnOrderConfig, mapSnOrder, SnFrameworkOrdersData } from '../snFrameworkOrders';
import { useSnExternalOrders } from '../useSnExternalOrders';
import SecuredArea from '@components/security/SecuredArea';
import { SecuredConfig } from '@components/security/securedDecoration';

interface ComponentProps {
  initialData?: SnFrameworkOrdersData;
  supplierNotificationId: string;
  companyId: string;
  supplierId: string;
  customerLocationId: string;
  readonly configuration: SnPartyConfigDTO;
  // PAP integration type (from the SN feature config). PAP picker activates only for NOTINO_PAP — same rule as create.
  readonly externalOrdersApiType?: ExternalOrdersApiTypeEnum;
  readonly disabled?: boolean;
  // Opt-in overlays (new detail page). Omitted on legacy → no overlay, legacy stays unchanged.
  readonly secured?: SecuredConfig;
  readonly fieldSecured?: (key: 'orderAmount' | 'orderValue' | 'itemNote', orderIndex: number, itemIndex?: number) => SecuredConfig | undefined;
}

// Module scope on purpose: a component declared inside the hook is a new type on every render of the
// owning page, and React remounts it — losing the form and refetching. See useSidebarControl.
const EditSupplierNotificationOrdersSidebar: React.FC<ComponentProps & SidebarControlProps> = ({
  initialData,
  supplierNotificationId,
  companyId,
  supplierId,
  customerLocationId,
  configuration,
  externalOrdersApiType,
  disabled = false,
  secured,
  fieldSecured,
  visible,
  onHide,
  onComplete,
}) => {
  const { tr } = useTranslationLgs();
  const { BackendValidationComponent, setBeValidationErrors, clear } = useBEValidation();
  const { updateNotificationSupplierUpdateDataForCargo } = useContext(ApiContext);

  const { externalOrders, externalIdPresent } = useSnExternalOrders(externalOrdersApiType, companyId, supplierId, customerLocationId);

  const ordersConfig = generateSnOrderConfig(configuration);

  const form = useForm<SnFrameworkOrdersData>(
    createOrdersComponentValidationSchema(ordersConfig, true, tr, externalIdPresent),
    initialData ?? { orders: [] },
    () => {
      updateNotificationSupplierUpdateDataForCargo(
        supplierNotificationId,
        { orders: form.values.orders.map(o => mapSnOrder(o, externalIdPresent)) },
        () => {
          onHide();
          onComplete();
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
    },
    false,
    false,
  );

  const orders = (
    <OrdersComponent
      orders={form.values.orders}
      onChange={v =>
        form.useProduce(d => {
          d.orders = v;
        })
      }
      config={ordersConfig}
      validationErrors={form.validationErrors}
      companyId={companyId}
      externalOrders={externalOrders}
      externalIdPresent={externalIdPresent}
      showNoOrdersNow={false}
      disableOrdersOutsideCurrentDate={true}
      disabled={disabled}
      fieldSecured={fieldSecured}
      dataTestIdPrefix={'sn'}
      buildOrderTestId={buildSnOrderTestId}
      buildManUnitTestId={buildSnManUnitTestId}
    />
  );

  return (
    <RingilSidebar
      visible={visible}
      heading={tr(`EditSupplierNotificationOrdersSidebar.updateSupplierNotification`, `Update supplier notification`)}
      onBack={() => {
        clear();
        onHide();
      }}
      onContinue={() => form.validateAndSend()}
    >
      {visible && (
        <>
          <BackendValidationComponent />
          {secured ? <SecuredArea {...secured}>{orders}</SecuredArea> : orders}
        </>
      )}
    </RingilSidebar>
  );
};

export const useCreateEditSupplierNotificationOrdersSidebar = (onComplete: () => void) => {
  const { sidebarProps, mountKey, setVisible } = useSidebarControl(onComplete);
  return { EditSupplierNotificationOrdersSidebar, sidebarProps, mountKey, setVisible };
};
